/**
 * useRetention hooks (Spec 201 — US1)
 *
 * React Query hooks for GFS policies + recycle bin.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { retentionApi, GfsRetentionPolicy, RecycleBinEntry, BackupEntityType } from '../api/retention';

const POLICIES = ['backup', 'retention-policies'] as const;
const BIN = ['backup', 'recycle-bin'] as const;

export function useRetentionPolicies() {
  return useQuery<GfsRetentionPolicy[]>({ queryKey: POLICIES, queryFn: retentionApi.listPolicies });
}

export function useCreateRetentionPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<GfsRetentionPolicy>) => retentionApi.createPolicy(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: POLICIES }),
  });
}

export function useRecycleBin() {
  return useQuery<RecycleBinEntry[]>({ queryKey: BIN, queryFn: retentionApi.listRecycleBin });
}

export function useRecycleBinActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: BIN });
  const restore = useMutation({
    mutationFn: (e: { entityType: BackupEntityType; runId: string }) =>
      retentionApi.restoreFromBin(e.entityType, e.runId),
    onSuccess: invalidate,
  });
  const purge = useMutation({
    mutationFn: (e: { entityType: BackupEntityType; runId: string }) =>
      retentionApi.purge(e.entityType, e.runId),
    onSuccess: invalidate,
  });
  const setHold = useMutation({
    mutationFn: (e: { entityType: BackupEntityType; runId: string; hold: boolean }) =>
      retentionApi.setRunLegalHold(e.entityType, e.runId, e.hold),
    onSuccess: invalidate,
  });
  return { restore, purge, setHold };
}
